import {
    type AfterViewInit,
    Component,
    type ElementRef,
    ViewChild,
    inject,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import {
    AuthServerProvider,
    LoginService,
    StateStorageService,
} from "../../core";
import { OAuthHandoffService } from "../../core/login/oauth-handoff.service";
import {
    EmitEvent,
    Event,
    EventService,
} from "../../shared/events/events.service";

@Component({
    templateUrl: "./verify-two-factor.component.html",
    standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
})
export class VerifyTwoFactorComponent implements AfterViewInit {
    @ViewChild("verificationCodeField", { static: true })
    public verificationCodeField: ElementRef;

    mfaType: string = "TWILIO";
    saving: boolean = false;
    authenticationError: boolean = false;
    verificationCode: string = "";
    attemptCount: number = 0;
    maxAttempts: number = 0;
    isLocked: boolean = false;
    errorMessage: string = "";
    username: string = "";

    private readonly loginService = inject(LoginService);
    private readonly authJWTService = inject(AuthServerProvider);
    private readonly stateStorageService = inject(StateStorageService);
    private readonly eventService = inject(EventService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly oauthHandoffService = inject(OAuthHandoffService);

    ngAfterViewInit(): void {
        this.authJWTService.logout().subscribe();
        this.eventService.emit(new EmitEvent(Event.REFRESH_NAV, "true"));
        this.verificationCodeField.nativeElement.focus();

        this.activatedRoute.queryParams.subscribe((params) => {
            if (params["mfaType"]) {
                this.mfaType = params["mfaType"];
            }
            if (params["username"]) {
                this.username = params["username"];
            }
        });
    }

    verifyTwoFactor(): void {
        this.saving = true;
        this.loginService
            .verifyTwoFactor(this.username, this.verificationCode)
            .subscribe({
                next: () => {
                    this.authenticationError = false;
                    this.eventService.emit(
                        new EmitEvent(Event.REFRESH_NAV, "true"),
                    );

                    if (this.oauthHandoffService.hasPendingHandoff()) {
                        this.performOAuthHandoff();
                        return;
                    }

                    const redirect = this.stateStorageService.getUrl();
                    if (redirect) {
                        this.stateStorageService.storeUrl(null);
                        this.router.navigateByUrl(redirect);
                    } else {
                        this.router.navigate(["/"]);
                    }
                },
                error: (err) => {
                    console.error(err);
                    if (err.error) {
                        this.attemptCount = err.error.attemptCount || 0;
                        this.maxAttempts = err.error.maxAttempts || 3;
                        this.isLocked = err.error.locked || false;
                        this.errorMessage =
                            err.error.message ||
                            "Verification failed! Please check your credentials and try again.";

                        if (this.attemptCount >= this.maxAttempts) {
                            this.isLocked = true;
                            setTimeout(() => {
                                this.router.navigate(["/login"]);
                            }, 3000);
                        }
                    }

                    this.authenticationError = true;
                    this.saving = false;
                    this.verificationCode = "";
                },
            });
    }

    private performOAuthHandoff(): void {
        this.oauthHandoffService.completeHandoff().subscribe({
            error: (err) => {
                console.error("OAuth handoff failed", err);
                this.authenticationError = true;
                this.errorMessage = "OAuth handoff failed. Please try again.";
                this.saving = false;
            },
        });
    }
}
